import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  font-size: 10px;
  margin-top: 16px;
`;

const Heading = styled.h2`
  font-size: 10px;
  margin: 0;
`;

const Paragraph = styled.p`
  font-size: 10px;
  margin: 0;
`;

function MicrosoftCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleMicrosoftCallback } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple executions (React StrictMode in development runs effects twice)
    if (processedRef.current) {
      return;
    }

    const authenticate = async () => {
      try {
        // Check if user is already logged in
        const existingToken = localStorage.getItem('token');
        if (existingToken) {
          // User is already logged in, redirect to dashboard
          console.log('User already logged in, redirecting to dashboard...');
          processedRef.current = true;
          navigate('/', { replace: true });
          return;
        }

        const code = searchParams.get('code');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (errorParam) {
          setError(errorDescription || 'Microsoft authentication failed');
          setLoading(false);
          processedRef.current = true;
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        if (!code) {
          setError('Authorization code not found');
          setLoading(false);
          processedRef.current = true;
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Mark as processed immediately to prevent duplicate calls
        processedRef.current = true;

        // Exchange code for token and login
        const result = await handleMicrosoftCallback(code);

        if (result.success) {
          toast.success('Login successful!');
          // Clear URL parameters to prevent re-processing on refresh
          window.history.replaceState({}, document.title, '/auth/microsoft/callback');
          navigate('/', { replace: true });
        } else {
          // Check if it's the "code already redeemed" error
          const errorMessage = result.error || 'Microsoft authentication failed';
          if (errorMessage.includes('AADSTS54005') || errorMessage.includes('already redeemed')) {
            // If code was already used, try to get user from token in localStorage
            const existingToken = localStorage.getItem('token');
            if (existingToken) {
              toast.success('Already logged in!');
              navigate('/', { replace: true });
              return;
            }
          }
          setError(errorMessage);
          setLoading(false);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
      } catch (err) {
        console.error('Microsoft callback error:', err);
        setError('An unexpected error occurred');
        setLoading(false);
        processedRef.current = true;
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    authenticate();
  }, [searchParams, handleMicrosoftCallback, navigate]);

  return (
    <Container>
      <Card>
        {loading ? (
          <>
            <LoadingSpinner />
            <Heading>Completing sign in...</Heading>
            <Paragraph>Please wait while we sign you in with Microsoft.</Paragraph>
          </>
        ) : (
          <>
            {error ? (
              <>
                <Heading style={{ color: '#dc2626', marginBottom: '16px' }}>Authentication Error</Heading>
                <ErrorMessage>{error}</ErrorMessage>
                <Paragraph style={{ marginTop: '16px', color: '#6b7280' }}>
                  Redirecting to login page...
                </Paragraph>
              </>
            ) : (
              <>
                <Heading>Sign in successful!</Heading>
                <Paragraph>Redirecting...</Paragraph>
              </>
            )}
          </>
        )}
      </Card>
    </Container>
  );
}

export default MicrosoftCallback;

